import {
    CreateExtArgs,
    Extension,
    ExtensionAPI,
    DmApis,
    DocumentDataTypes,
    CreateExtensionArgument,
    DmStore,
    createStore,
    FragmentStoreMap,
    createFragmentStore,
    ModelsInitializationType,
    pointerUtils
} from '@wix/document-manager-core'
import type {DalValue, Pointer, FragmentPointer} from '@wix/document-services-types'
import type {
    FragmentMetadata,
    Fragment,
    DataItem
} from '@wix/ambassador-document-management-fragments-v1-fragment-snapshot/types'
import _ from 'lodash'
import {schemas} from '@wix/dm-fragment-schemas'
import {addDefaultMetaData} from '../../utils/dataUtils'
import {ReportableError} from '@wix/document-manager-utils'

const {getPointer} = pointerUtils

interface FragmentsAPI extends ExtensionAPI {
    addFragmentIndex(fragmentInstance: FragmentMetadata): void

    mergeFragmentIndex(fragmentInstance: FragmentMetadata): void

    mergeFragment(fragment: Fragment): void

    add(item: DalValue, namespace: string, fragmentType: string, fragmentInstance: string): Pointer

    removeFragment(pointer: Pointer): void

    updateFragment(pointer: Pointer, data: DataItem): void
}

interface FragmentsExtensionAPI extends ExtensionAPI {
    fragmentIndex: {
        list(fragmentType: string): FragmentPointer[]
    }
    fragments: FragmentsAPI
}

const FRAGMENT_INDEX = {
    TYPE: 'FragmentIndex',
    INSTANCE: 'FragmentIndexSingleton'
}

/**
 * @returns {Extension}
 */
const createExtension = ({dsConfig, logger}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = ({dal}: CreateExtArgs): FragmentsExtensionAPI => {
        const getFragmentIndexPointer = (fragmentInstance: FragmentMetadata): FragmentPointer => {
            if (!fragmentInstance.name || !fragmentInstance.type) {
                throw new Error('Fragment instance should have a name and a type')
            }
            const pointer: FragmentPointer = {
                id: fragmentInstance.name,
                type: fragmentInstance.type,
                fragmentType: FRAGMENT_INDEX.TYPE,
                fragmentInstance: FRAGMENT_INDEX.INSTANCE
            }
            return pointer
        }

        const isFragmentInstanceValid = (fragmentType: string, fragmentInstance: string) => {
            if (!dsConfig.supportedFragments?.[fragmentType]?.singletonInstance) {
                return true
            }
            return dsConfig.supportedFragments?.[fragmentType]?.singletonInstance === fragmentInstance
        }

        return {
            fragmentIndex: {
                list: (fragmentType: string) => {
                    return dal.getItemsInNamespace(FRAGMENT_INDEX.TYPE, fragmentType)
                }
            },
            fragments: {
                addFragmentIndex: (fragmentInstance: FragmentMetadata) => {
                    const pointer: FragmentPointer = getFragmentIndexPointer(fragmentInstance)
                    if (!isFragmentInstanceValid(pointer.type, pointer.id)) {
                        throw new Error(`${pointer.type} is a singleton, the provided fragmentInstance is not valid`)
                    }
                    if (!dal.get(pointer)) {
                        dal.set(pointer, {
                            type: 'FragmentIndexDataItem',
                            metaData: {fragmentInstance: FRAGMENT_INDEX.INSTANCE}
                        })
                    }
                },
                mergeFragmentIndex: (fragmentInstance: FragmentMetadata) => {
                    const pointer: FragmentPointer = getFragmentIndexPointer(fragmentInstance)
                    if (!dal.get(pointer)) {
                        const fragmentStoreMap: FragmentStoreMap = new Map()
                        const fragmentStore: DmStore = createStore()
                        fragmentStore.set(pointer, {
                            type: 'FragmentIndexDataItem',
                            metaData: {fragmentInstance: FRAGMENT_INDEX.INSTANCE}
                        })
                        fragmentStoreMap.set(pointer.fragmentType, fragmentStore)
                        dal.mergeToApprovedStore(createFragmentStore(fragmentStoreMap), 'add-fragment-index')
                    }
                },

                mergeFragment: (fragment: Fragment) => {
                    if (fragment.type === FRAGMENT_INDEX.TYPE) {
                        logger.captureError(
                            new ReportableError({
                                errorType: 'forbiddenFragmentIndexOperation',
                                message: 'attempt to merge FragmentIndex with mergeFragment',
                                extras: {
                                    value: fragment.content,
                                    oldValue: dal.get({
                                        id: 'AppStateSingleton',
                                        type: 'AppState',
                                        fragmentType: fragment.type,
                                        fragmentInstance: fragment.name
                                    })
                                }
                            })
                        )
                        throw new Error('FragmentIndex should be handled by mergeFragmentIndex, not mergeFragment')
                    }
                    const fragmentStoreMap: FragmentStoreMap = new Map()
                    _.forEach(fragment.content, (namespaceData, type) => {
                        _.forEach(namespaceData?.namespaceItems, (data, id) => {
                            const pointer = {
                                id,
                                type
                            }
                            const fragmentStore: DmStore = createStore()
                            fragmentStore.set(pointer, data.value)
                            fragmentStoreMap.set(fragment.type!, fragmentStore)
                        })
                    })
                    dal.mergeToApprovedStore(createFragmentStore(fragmentStoreMap), 'add-fragment')
                },

                add: (item: DalValue, namespace: string, fragmentType: string, fragmentInstance: string) => {
                    if (fragmentType === FRAGMENT_INDEX.TYPE) {
                        logger.captureError(
                            new ReportableError({
                                errorType: 'forbiddenFragmentIndexOperation',
                                message: 'attempt to add FragmentIndex with add',
                                extras: {
                                    value: item,
                                    oldValue: dal.get({
                                        id: item.id,
                                        type: item.type,
                                        fragmentType,
                                        fragmentInstance
                                    })
                                }
                            })
                        )
                        throw new Error('FragmentIndex should be handled by addFragmentIndex, not add')
                    }
                    if (!isFragmentInstanceValid(fragmentType, fragmentInstance)) {
                        throw new Error(`${fragmentType} is a singleton, the provided fragmentInstance is not valid`)
                    }
                    const itemPointer = getPointer(item.id, namespace, {
                        fragmentType,
                        fragmentInstance
                    })
                    const existingMetaData = item.metaData ?? {}
                    const metaDataObj = {
                        metaData: {...existingMetaData, fragmentInstance}
                    }
                    const newItem = {...item, ...metaDataObj}
                    addDefaultMetaData(newItem, undefined, namespace)
                    dal.set(itemPointer, newItem)
                    return itemPointer
                },

                removeFragment: (pointer: Pointer) => {
                    dal.remove(pointer)
                },

                updateFragment: (pointer: Pointer, data: DataItem) => {
                    dal.set(pointer, data.value)
                }
            }
        }
    }

    const initializeModels = (_initialStore: DmStore, _initialModelData: ModelsInitializationType, dmApis: DmApis) => {
        if (!dmApis.coreConfig.experimentInstance.isOpen('dm_fragments')) {
            return
        }
        _.forEach(dmApis.coreConfig.supportedFragments, (fragmentConfig, fragmentType) => {
            if (fragmentType === FRAGMENT_INDEX.TYPE) {
                return
            }
            if (!schemas[fragmentType]) {
                logger.captureError(
                    new ReportableError({
                        errorType: 'noSchemaForFragmentType',
                        message: `No schema found for fragment type: ${fragmentType}`
                    })
                )
            }
            dmApis.coreConfig.validationService?.registerFragmentSchemas(fragmentType, schemas[fragmentType])
            dmApis.coreConfig.validationService?.registerFragmentSchemas(FRAGMENT_INDEX.TYPE, {
                [fragmentType]: schemas[FRAGMENT_INDEX.TYPE].FragmentType
            })
        })
        logger.interactionEnded('fragments-extension-initialize-models', {
            extras: Object.keys(dmApis.coreConfig.supportedFragments ?? {})
        })
    }

    const getDocumentDataTypes = (): DocumentDataTypes =>
        _.mapValues(dsConfig.supportedFragments, () => ({hasSignature: true}))

    return {
        name: 'fragments',
        createExtensionAPI,
        initializeModels,
        getDocumentDataTypes
    }
}

export {createExtension, FragmentsExtensionAPI, FragmentsAPI}
